<template>
  <div class="security-settings">
    <!-- Password change section -->
    <div class="info-card">
      <h3>{{ t('account.security.changePassword') }}</h3>
      <p class="section-description">
        {{ t('account.security.changePasswordDescription') }}
      </p>
      <form
        class="security-form"
        @submit.prevent="handleChangePassword"
      >
        <div class="form-group">
          <label for="currentPassword">{{ t('account.security.currentPassword') }}</label>
          <div class="password-input-container">
            <input
              id="currentPassword"
              v-model="passwordForm.currentPassword"
              :type="showCurrentPassword ? 'text' : 'password'"
              autocomplete="current-password"
              :placeholder="t('account.security.currentPasswordPlaceholder')"
            >
            <button
              type="button"
              class="password-toggle-btn"
              @click="showCurrentPassword = !showCurrentPassword"
              :title="showCurrentPassword ? t('login.hidePassword') : t('login.showPassword')"
            >
              <svg
                v-if="showCurrentPassword"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line
                  x1="1"
                  y1="1"
                  x2="23"
                  y2="23"
                />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                />
              </svg>
            </button>
          </div>
        </div>
        <div class="form-group">
          <label for="newPassword">{{ t('account.security.newPassword') }}</label>
          <div class="password-input-container">
            <input
              id="newPassword"
              v-model="passwordForm.newPassword"
              :type="showNewPassword ? 'text' : 'password'"
              autocomplete="new-password"
              :placeholder="t('account.security.newPasswordPlaceholder')"
            >
            <button
              type="button"
              class="password-toggle-btn"
              @click="showNewPassword = !showNewPassword"
              :title="showNewPassword ? t('login.hidePassword') : t('login.showPassword')"
            >
              <svg
                v-if="showNewPassword"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line
                  x1="1"
                  y1="1"
                  x2="23"
                  y2="23"
                />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                />
              </svg>
            </button>
          </div>
        </div>
        <div class="form-group">
          <label for="confirmPassword">{{ t('account.security.confirmPassword') }}</label>
          <div class="password-input-container">
            <input
              id="confirmPassword"
              v-model="passwordForm.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              autocomplete="new-password"
              :class="{ invalid: passwordsDoNotMatch }"
              :placeholder="t('account.security.confirmPasswordPlaceholder')"
            >
            <button
              type="button"
              class="password-toggle-btn"
              @click="showConfirmPassword = !showConfirmPassword"
              :title="showConfirmPassword ? t('login.hidePassword') : t('login.showPassword')"
            >
              <svg
                v-if="showConfirmPassword"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line
                  x1="1"
                  y1="1"
                  x2="23"
                  y2="23"
                />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                />
              </svg>
            </button>
          </div>
          <p
            v-if="passwordsDoNotMatch"
            class="validation-error"
          >
            {{ t('registration.step_account_setup.passwordsDoNotMatch') }}
          </p>
        </div>
        <p
          v-if="passwordError"
          class="error-message"
        >
          {{ passwordError }}
        </p>
        <p
          v-if="passwordSuccess"
          class="success-message"
        >
          {{ passwordSuccess }}
        </p>
        <button
          type="submit"
          class="submit-btn"
          :disabled="isChangingPassword || !isPasswordFormValid"
        >
          {{ isChangingPassword ? t('account.security.changingPassword') : t('account.security.changePasswordButton') }}
        </button>
      </form>
    </div>

    <!-- Reset password section (forgot password - send reset link) -->
    <div class="info-card">
      <h3>{{ t('account.security.resetPassword') }}</h3>
      <p class="section-description">
        {{ t('account.security.resetPasswordDescription') }}
      </p>
      <div class="security-form">
        <p
          v-if="resetPasswordError"
          class="error-message"
        >
          {{ resetPasswordError }}
        </p>
        <p
          v-if="resetPasswordSuccess"
          class="success-message"
        >
          {{ resetPasswordSuccess }}
        </p>
        <button
          type="button"
          class="submit-btn"
          :disabled="isRequestingReset"
          @click="handleRequestPasswordReset"
        >
          {{ isRequestingReset ? t('account.security.sendingResetEmail') : t('account.security.sendResetEmailButton') }}
        </button>
      </div>
    </div>

    <!-- Primary email change section -->
    <div class="info-card">
      <h3>{{ t('account.security.changePrimaryEmail') }}</h3>
      <p class="section-description">
        {{ t('account.security.changePrimaryEmailDescription') }}
      </p>
      <form
        class="security-form"
        @submit.prevent="handleChangeEmail"
      >
        <div class="form-group">
          <label for="newEmail">{{ t('account.security.newEmail') }}</label>
          <input
            id="newEmail"
            v-model="emailForm.newEmail"
            type="email"
            autocomplete="email"
            :placeholder="t('account.security.newEmailPlaceholder')"
          >
        </div>
        <div class="form-group">
          <label for="confirmEmail">{{ t('account.security.confirmEmail') }}</label>
          <input
            id="confirmEmail"
            v-model="emailForm.confirmEmail"
            type="email"
            autocomplete="email"
            :class="{ invalid: emailsDoNotMatch }"
            :placeholder="t('account.security.confirmEmailPlaceholder')"
          >
          <p
            v-if="emailsDoNotMatch"
            class="validation-error"
          >
            {{ t('account.security.emailsDoNotMatch') }}
          </p>
        </div>
        <p
          v-if="emailError"
          class="error-message"
        >
          {{ emailError }}
        </p>
        <p
          v-if="emailSuccess"
          class="success-message"
        >
          {{ emailSuccess }}
        </p>
        <button
          type="submit"
          class="submit-btn"
          :disabled="isChangingEmail || !isEmailFormValid"
        >
          {{ isChangingEmail ? t('account.security.sendingVerification') : t('account.security.changeEmailButton') }}
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { r7eBaseApp } from '../services/index.js';

const { t } = useI18n();

const passwordForm = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});

const emailForm = ref({
  newEmail: '',
  confirmEmail: ''
});

const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);

const passwordError = ref('');
const passwordSuccess = ref('');
const resetPasswordError = ref('');
const resetPasswordSuccess = ref('');
const emailError = ref('');
const emailSuccess = ref('');

const isChangingPassword = ref(false);
const isRequestingReset = ref(false);
const isChangingEmail = ref(false);

const passwordsDoNotMatch = computed(() =>
  passwordForm.value.newPassword.length > 0 &&
  passwordForm.value.confirmPassword.length > 0 &&
  passwordForm.value.newPassword !== passwordForm.value.confirmPassword
);

const emailsDoNotMatch = computed(() =>
  emailForm.value.newEmail.length > 0 &&
  emailForm.value.confirmEmail.length > 0 &&
  emailForm.value.newEmail !== emailForm.value.confirmEmail
);

const isPasswordFormValid = computed(() =>
  passwordForm.value.currentPassword.length > 0 &&
  passwordForm.value.newPassword.length > 0 &&
  passwordForm.value.confirmPassword.length > 0 &&
  !passwordsDoNotMatch.value
);

const isEmailFormValid = computed(() =>
  emailForm.value.newEmail.length > 0 &&
  emailForm.value.confirmEmail.length > 0 &&
  !emailsDoNotMatch.value
);

const handleChangePassword = async () => {
  if (!isPasswordFormValid.value) return;

  passwordError.value = '';
  passwordSuccess.value = '';
  isChangingPassword.value = true;

  try {
    await r7eBaseApp.changePassword(
      passwordForm.value.currentPassword,
      passwordForm.value.newPassword
    );

    passwordSuccess.value = t('account.security.passwordChangedSuccess');
    passwordForm.value = { currentPassword: '', newPassword: '', confirmPassword: '' };
  } catch (err) {
    const msg = err.response?.data?.message || err.response?.data?.error || err.message;
    passwordError.value = msg || t('account.security.passwordChangeFailed');
  } finally {
    isChangingPassword.value = false;
  }
};

const handleRequestPasswordReset = async () => {
  resetPasswordError.value = '';
  resetPasswordSuccess.value = '';
  isRequestingReset.value = true;

  try {
    await r7eBaseApp.requestPasswordReset();

    resetPasswordSuccess.value = t('account.security.resetEmailSent');
  } catch (err) {
    const msg = err.response?.data?.message || err.response?.data?.error || err.message;
    resetPasswordError.value = msg || t('account.security.resetPasswordFailed');
  } finally {
    isRequestingReset.value = false;
  }
};

const handleChangeEmail = async () => {
  if (!isEmailFormValid.value) return;

  emailError.value = '';
  emailSuccess.value = '';
  isChangingEmail.value = true;

  try {
    await r7eBaseApp.changePrimaryEmail(emailForm.value.newEmail);

    emailSuccess.value = t('account.security.emailVerificationSent');
    emailForm.value = { newEmail: '', confirmEmail: '' };
  } catch (err) {
    const msg = err.response?.data?.message || err.response?.data?.error || err.message;
    emailError.value = msg || t('account.security.emailChangeFailed');
  } finally {
    isChangingEmail.value = false;
  }
};
</script>

<style scoped>
.security-settings {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.info-card {
  background-color: var(--color-frame-bg);
  border: 1px solid var(--color-border);
  padding: 20px;
  border-radius: 5px;
}

.info-card h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1em;
  color: var(--color-text);
}

.section-description {
  margin: 0 0 1rem 0;
  font-size: 0.95em;
  color: var(--color-text);
  opacity: 0.9;
}

.security-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.form-group label {
  font-weight: 500;
  color: var(--color-text);
}

.form-group input {
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 1em;
  background: var(--color-bg);
  color: var(--color-text);
}

.form-group input.invalid {
  border-color: var(--color-danger);
}

.form-group input:focus {
  outline: 2px solid var(--color-border);
  outline-offset: 2px;
}

.password-input-container {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}

.password-input-container input {
  flex: 1;
}

.password-toggle-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--color-text);
  opacity: 0.7;
}

.password-toggle-btn:hover {
  opacity: 1;
}

.validation-error {
  color: var(--color-danger);
  font-size: 0.9em;
  margin: 0;
}

.error-message {
  color: var(--color-danger);
  margin: 0;
}

.success-message {
  color: var(--color-safe);
  margin: 0;
}

.submit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  background: var(--color-safe);
  color: var(--color-text-light);
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 160ms ease, filter 160ms ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.10);
  align-self: flex-start;
}

.submit-btn:hover:not(:disabled) {
  filter: brightness(0.96);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-btn:focus-visible {
  outline: 2px solid var(--color-border);
  outline-offset: 2px;
}
</style>
